import React, { useState, useEffect } from "react";
import { Button, Image } from "react-bootstrap";
import { ActionHandlerFunction, CredentialInformation, WebAuthnKeyAuthenticate } from "@preveil-api";
import {
  authenticateWebAuthnKey, AuthenticationTypes, getWebAuthErrorMessage, LoginWebUIActionTypes, dayjs, GlobalConstants, useCountdown, SettingsMessages, MessageHandlerDisplayType, MessageToastTypes,
  Message, useAppDispatch
} from "src/common";
import { InfoPopover } from "src/components";
import { uiActions } from "src/store";
import passkeyImage from "src/assets/images/account/2fa/passkey-image.svg";
import securityKeyImage from "src/assets/images/account/2fa/securitykey-image.svg";
import webAuthKeyImage from "src/assets/images/account/2fa/webauthkey-image.svg";

type AllProps = {
  webauth_info: CredentialInformation;
  handleAction: ActionHandlerFunction;
  forgot_password?: boolean;
}

// Description: Component for authenticating an existing WebAuthn Key with the browser
function LoginWebAuthKeyComponent(props: AllProps) {
  const { handleAction, webauth_info, forgot_password } = props;
  const dispatch = useAppDispatch();
  const decoded_context = JSON.parse(atob(webauth_info.context));
  const title = decoded_context.type === AuthenticationTypes.PASSKEY ? "Passkey" : decoded_context.type === AuthenticationTypes.SECURITY_KEY ? "Security Key" : "Passkey or Security Key";
  const image = decoded_context.type === AuthenticationTypes.PASSKEY ? passkeyImage : decoded_context.type === AuthenticationTypes.SECURITY_KEY ? securityKeyImage : webAuthKeyImage;
  const expires = dayjs().add(GlobalConstants.WEB_AUTH_CHALLENGE_TIMER, "minutes").format("YYYY-MM-DD HH:mm:ss");
  const [expired, setExpired] = useState<boolean>(false);
  const [days, hours, minutes, seconds] = useCountdown(expires);

  // Description: Get the current webauthn key when the component mounts - as per a request from randy
  // to reduce number of user clicks.
  useEffect(() => {
    getCurrentWebAuthKey();
  }, []);

  // Description: Detects if 10 minutes have passed since the challenge was created
  useEffect(() => {
    (Number(days) + Number(hours) + Number(minutes) + Number(seconds)) <= 0 && setExpired(true);
  }, [seconds]);

  // Description: Get the current passkey through the webauthn API
  // DOCS: https://webauthn.guide/#registration
  async function getCurrentWebAuthKey() {
    authenticateWebAuthnKey(webauth_info).then((params: WebAuthnKeyAuthenticate | undefined) => { 
      handleAction({ actionType: LoginWebUIActionTypes.SubmitWebAuthnKey, params: { web_auth_key: params, type: decoded_context.type} });
    }).catch((error: any) => {
      // "NotAllowedError" is thrown when the user cancels the request or if the request is timed out - we don't know which one so displaying a warning.
      if (error.name === "NotAllowedError" || error.name === "AbortError" || error.name === "InvalidStateError") {
        dispatch(uiActions.handleSetMessage(new Message(getWebAuthErrorMessage(error), MessageHandlerDisplayType.toaster, MessageToastTypes.warning)));
      } else {
        handleAction({ actionType: LoginWebUIActionTypes.PageError, params: getWebAuthErrorMessage(error) });
      }
    });
  }

  // Description: Expiration Message Panel
  function VerificationCodeExpired() {
    return <>
      <p>
        Your Session has expired. Please click <b>Back</b> to restart the process.
      </p>
      <div className="btn-panel">
        <Button variant="outline-secondary" type="button"
          onClick={() => handleAction({ actionType: LoginWebUIActionTypes.ResetLogin })}>Back</Button>
      </div>
    </>;
  }
  
  return <>
    {expired ? <VerificationCodeExpired /> : <>
      <h3>{title}</h3>
      <h4>{forgot_password ? "Please authenticate" : "Log into PreVeil"} with your {title} verification.</h4>
      <div className="webauthkey-message">
        <Image src={image}/>
        <p>Select continue for your FedRAMP compliant {forgot_password ? "authentication" : "login"}. <InfoPopover message={SettingsMessages.web_auth_tooltip} placement="top"/></p></div>
      <div className="btn-panel">
        <Button onClick={getCurrentWebAuthKey}>Continue</Button>
        <Button variant="outline-secondary" type="button" onClick={() => handleAction({ actionType: LoginWebUIActionTypes.ResetLogin })}>Cancel</Button>
      </div>
    </>}
  </>;
}

export default React.memo(LoginWebAuthKeyComponent);
